import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, API_CONFIG, HTTP_METHODS } from '../config/api';
import { authApiService } from './authApi';
import { toast } from 'sonner';

export interface PresignedUrlResponse {
  presignedUrl: string;
  fileKey: string;
  expiresIn: number;
}

export interface FileUploadResponse {
  id: number;
  fileName: string;
  originalName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  thumbnailUrl?: string;
  // Additional fields that might be returned by getFilesByEndgood API
  originalFileName?: string;
  fileKey?: string;
  contentType?: string;
  fileUrl?: string;
  thumbnailKey?: string;
}

export interface ConfirmUploadRequest {
  fileKey: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  sessionId?: string; // For tracking temporary uploads
}

export interface CleanupRequest {
  fileKeys: string[];
  sessionId?: string;
}

export interface ConfirmUploadResponse {
  success: boolean;
  message: string;
  data: FileUploadResponse;
}

/**
 * FileUploadApiService - Handles file uploads using presigned URLs
 * 
 * Flow:
 * 1. UI calls getPresignedUrl() to get a signed URL from backend
 * 2. UI uploads file directly to S3 using the presigned URL
 * 3. UI calls confirmUpload() to notify backend and generate thumbnails/metadata
 * 
 * Benefits:
 * - Direct S3 upload (faster, no server bottleneck)
 * - Secure (presigned URLs expire)
 * - Server generates thumbnails and stores metadata
 * - Real-time progress tracking
 */
class FileUploadApiService extends BaseApiService {
  // Step 1: Get presigned URL from backend
  async getPresignedUrl(file: File, category: string = 'drawing', folder?: string, keepOriginalName?: boolean): Promise<PresignedUrlResponse> {
    try {
      // Get current user ID for the X-User-ID header
      const user = authApiService.getUserInfo();
      const userId = user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Build the request body with folder parameter
      const requestBody: any = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: category
      };

      // Add folder parameter if provided
      if (folder) {
        requestBody.folder = folder;
      }

      // Add keepOriginalName parameter if provided
      if (keepOriginalName !== undefined) {
        requestBody.keepOriginalName = keepOriginalName;
      }

      const response = await this.makeRequest<ApiResponse<PresignedUrlResponse>>(
        API_ENDPOINTS.FILES.PRESIGNED_URL,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': userId.toString()
          }
        }
      );

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to get presigned URL');
      }
    } catch (error: any) {
      // console.error('Error getting presigned URL:', error);
      toast.error(`Failed to prepare upload for "${file.name}": ${error.message}`);
      throw error;
    }
  }

  // Step 2: Upload file directly to S3 using presigned URL with retry logic
  async uploadToS3(file: File, presignedUrl: string, onProgress?: (progress: number) => void, maxRetries: number = 3): Promise<void> {
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        await this.uploadToS3SingleAttempt(file, presignedUrl, onProgress);
        return; // Success, exit retry loop
        } catch (error: any) {
          attempt++;
          if (attempt >= maxRetries) {
            throw new Error(`Upload failed after ${maxRetries} attempts: ${error.message}`);
          }
        
        // Wait before retry (exponential backoff)
        const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s...
        // console.warn(`Upload attempt ${attempt} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  private async uploadToS3SingleAttempt(file: File, presignedUrl: string, onProgress?: (progress: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  // Step 3: Confirm upload with backend (generate thumbnails & store metadata)
  async confirmUpload(request: ConfirmUploadRequest): Promise<FileUploadResponse> {
    try {
      // Get current user ID for the X-User-ID header
      const user = authApiService.getUserInfo();
      const userId = user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Log request details for debugging
      // console.log('Making confirm upload request to:', API_ENDPOINTS.FILES.CONFIRM_UPLOAD);
      // console.log('Request payload:', request);
      // console.log('User ID:', userId);

      const response = await this.makeRequest<ApiResponse<FileUploadResponse>>(
        API_ENDPOINTS.FILES.CONFIRM_UPLOAD,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(request),
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': userId.toString()
          }
        }
      );

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to confirm upload');
      }
    } catch (error: any) {
      // console.error('Error confirming upload:', error);
      // console.error('Request that failed:', request);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        stack: error.stack
      });
      toast.error(`Failed to confirm upload for "${request.originalName}": ${error.message}`);
      throw error;
    }
  }

  // Complete upload flow: presigned URL -> S3 upload -> confirm
  async uploadFile(file: File, category: string = 'drawing', onProgress?: (progress: number) => void, folder?: string, keepOriginalName?: boolean): Promise<FileUploadResponse> {
    try {
      // Step 1: Get presigned URL
      const presignedData = await this.getPresignedUrl(file, category, folder, keepOriginalName);
      
      // Step 2: Upload to S3
      await this.uploadToS3(file, presignedData.presignedUrl, onProgress);
      
      // Step 3: Confirm upload with backend
      const confirmRequest: ConfirmUploadRequest = {
        fileKey: presignedData.fileKey,
        originalName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: category,
        sessionId: this.generateSessionId() // Add session ID for tracking
      };
      
      // Log the request payload for debugging
      // console.log('Confirm upload request:', confirmRequest);
      
      const result = await this.confirmUpload(confirmRequest);
      toast.success(`File "${file.name}" uploaded successfully`);
      return result;
    } catch (error: any) {
      // console.error('Error uploading file:', error);
      toast.error(`Failed to upload file "${file.name}": ${error.message}`);
      throw error;
    }
  }

  // Upload multiple files with progress tracking
  async uploadMultipleFiles(files: File[], category: string = 'drawing', onProgress?: (fileIndex: number, progress: number) => void, folder?: string): Promise<FileUploadResponse[]> {
    try {
      const uploadPromises = files.map(async (file, index) => {
        return this.uploadFile(file, category, (progress) => {
          if (onProgress) {
            onProgress(index, progress);
          }
        }, folder);
      });
      
      const results = await Promise.all(uploadPromises);
      toast.success(`${files.length} files uploaded successfully`);
      return results;
    } catch (error: any) {
      // console.error('Error uploading multiple files:', error);
      toast.error('Failed to upload some files');
      throw error;
    }
  }

  // Attach file to endgood
  async attachFileToEndgood(endgoodId: number, fileId: number): Promise<void> {
    try {
      await this.makeRequest<void>(
        API_ENDPOINTS.FILES.ATTACH_SINGLE(endgoodId.toString(), fileId.toString()),
        {
          method: HTTP_METHODS.POST
        }
      );
      toast.success('File attached to endgood successfully');
    } catch (error: any) {
      // console.error('Error attaching file to endgood:', error);
      toast.error('Failed to attach file to endgood');
      throw error;
    }
  }

  // Attach multiple files to endgood
  async attachFilesToEndgood(endgoodId: number, fileIds: number[], fileType: string = 'IMAGE'): Promise<void> {
    try {
      // Get current user ID for the X-User-ID header
      const user = authApiService.getUserInfo();
      const userId = user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Create the BulkFileAttachmentDto structure
      const bulkAttachmentDto = {
        fileAttachments: fileIds.map((fileId, index) => ({
          fileMetadataId: fileId,
          fileType: fileType,
          displayOrder: index + 1,
          isPrimary: index === 0 // First file is primary
        }))
      };

      await this.makeRequest<void>(
        API_ENDPOINTS.FILES.ATTACH_BULK(endgoodId.toString()),
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(bulkAttachmentDto),
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': userId.toString()
          }
        }
      );
      toast.success(`${fileIds.length} files attached to endgood successfully`);
    } catch (error: any) {
      // console.error('Error attaching files to endgood:', error);
      toast.error('Failed to attach files to endgood');
      throw error;
    }
  }

  // Remove file from endgood
  async removeFileFromEndgood(endgoodId: number, fileId: number): Promise<void> {
    try {
      await this.makeRequest<void>(
        API_ENDPOINTS.FILES.REMOVE(endgoodId.toString(), fileId.toString()),
        {
          method: HTTP_METHODS.DELETE
        }
      );
      toast.success('File removed from endgood successfully');
    } catch (error: any) {
      // console.error('Error removing file from endgood:', error);
      toast.error('Failed to remove file from endgood');
      throw error;
    }
  }

  // Get files for endgood
  async getFilesByEndgood(endgoodId: number): Promise<FileUploadResponse[]> {
    try {
      const response = await this.makeRequest<ApiResponse<FileUploadResponse[]>>(
        API_ENDPOINTS.FILES.GET_BY_ENDGOOD(endgoodId.toString()),
        {
          method: HTTP_METHODS.GET
        }
      );

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to get files');
      }
    } catch (error: any) {
      // console.error('Error getting files for endgood:', error);
      toast.error('Failed to get files for endgood');
      throw error;
    }
  }

  // Delete uploaded file (legacy method)
  async deleteFile(fileId: number): Promise<void> {
    try {
      await this.makeRequest<void>(
        API_ENDPOINTS.FILES.DELETE(fileId.toString()),
        {
          method: HTTP_METHODS.DELETE
        }
      );
      toast.success('File deleted successfully');
    } catch (error: any) {
      // console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
      throw error;
    }
  }

  // Get file URL for download/preview
  getFileUrl(filePath: string): string {
    // Check if filePath is valid
    if (!filePath || typeof filePath !== 'string') {
      // console.warn('getFileUrl: Invalid filePath provided:', filePath);
      return '';
    }
    return `${API_CONFIG.BASE_URL}${API_ENDPOINTS.FILES.DOWNLOAD(filePath)}`;
  }

  // Get thumbnail URL (if available)
  getThumbnailUrl(filePath: string): string {
    // Check if filePath is valid
    if (!filePath || typeof filePath !== 'string') {
      // console.warn('getThumbnailUrl: Invalid filePath provided:', filePath);
      return '';
    }
    
    // Assuming thumbnails are stored with a _thumb suffix
    const pathParts = filePath.split('.');
    const extension = pathParts.pop();
    const basePath = pathParts.join('.');
    return `${API_CONFIG.BASE_URL}${API_ENDPOINTS.FILES.DOWNLOAD(`${basePath}_thumb.${extension}`)}`;
  }

  // Cleanup temporary files (for abandoned uploads)
  async cleanupTempFiles(fileKeys: string[], sessionId?: string): Promise<void> {
    try {
      // Get current user ID for the X-User-ID header
      const user = authApiService.getUserInfo();
      const userId = user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      await this.makeRequest<void>(
        API_ENDPOINTS.FILES.CLEANUP_TEMP,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify({
            fileKeys,
            sessionId
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': userId.toString()
          }
        }
      );
      // console.log(`Cleaned up ${fileKeys.length} temporary files`);
    } catch (error: any) {
      // console.error('Error cleaning up temporary files:', error);
      // Don't show toast for cleanup errors as they're not user-facing
    }
  }

  // Generate session ID for tracking temporary uploads
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const fileUploadApiService = new FileUploadApiService();
